import { Action, createAction, handleActions } from 'redux-actions'
import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'
import { oc } from 'ts-optchain'

import * as Bridge from '@/api/BridgeApi'
import {
  DEBIT_CARD_API_URL,
  ezlInquiry,
  fetchDebitCardInfo,
  IDebitCardEzlDeliveryCompleteRequest,
  IDebitCardEzlInquiryRequest,
  IDebitCardShowInfoRequest,
  IDebitCardShowInfoResponse,
  inquiryEzlCardIssuanceStatus,
  isEzlDeliveryComplete,
} from '@/api/DebitCardApi'
import { NOTICE_ALERT_TYPE } from '@/components/common/layer/Notice/NoticeLayer'
import {
  DEBIT_CARD_ROUTES,
  RESET_DEBIT_CARD_PIN_ROUTE_QUERY,
  STATUS_CODE,
} from '@/domains/CommonDomain'
import {
  DAILY_LIMIT_AMOUNT,
  DebitCardBrand,
  DebitCardStatusTypeCode,
  EmptyDebitCardInfo,
  EzlCardStatus,
  IDebitCardEzlAvailableIssueResponse,
  IDebitCardEzlInquiryResponse,
  IDebitCardInfo,
  IEzlCardDetail,
  IS_DOMESTIC_USE,
  ShippingStatusTypeCode,
  SINGLE_TRANSACTION_LIMIT_AMOUNT,
} from '@/domains/DebitCardDomain'
import { IRootState } from '@/modules'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { debitCardIssueSelector } from '@/modules/debit-card/DebitCardIssueModule'
import { createFetchAction, createPostAction } from '@/utils/actionUtils'
import { IRequestAuthResult } from '@/utils/bridgeUtils/features/requestAuth'
import {
  HostType,
  IRequestPinAuthResult,
  RequestPinAuthAction,
} from '@/utils/bridgeUtils/features/requestPinAuth'
import { getCancellableLastDate, getDifferenceInDays } from '@/utils/dateUtils'
import { scrollToTop } from '@/utils/domUtils'
import { newError } from '@/utils/errorUtils'
import { goBack, push } from '@/utils/historyUtils'
import { toInteger } from '@/utils/numberUtils'
import { apiFailureReducer, apiSuccessReducer, reset } from '@/utils/reducerUtils'

interface IChangeUsageOptionAction {
  usageType: string
  isUse: boolean
}

interface IChangeTransactionLimitAction {
  transactionType: string
  amount: string
}

export interface IDebitCardDetailView {
  isDomesticUse: boolean
  isOverseaUse: boolean
  singleTransactionLimitAmount: string
  dailyLimitAmount: string
  myCardIndex: number
}

export interface IDebitCardDetailIdentity {
  debitCardNo: string
  acctNo: string
}

interface IDebitCardDetailState extends IDebitCardShowInfoResponse {
  debitCardDetailIdentity: IDebitCardDetailIdentity
  view: IDebitCardDetailView
}

export const defaultDebitCardDetailIdentity: IDebitCardDetailIdentity = {
  acctNo: '',
  debitCardNo: '',
}

export const defaultViewState: IDebitCardDetailView = {
  isDomesticUse: false,
  isOverseaUse: false,
  singleTransactionLimitAmount: '0',
  dailyLimitAmount: '0',
  myCardIndex: 0,
}

export const initialState: IDebitCardDetailState = {
  debitCardInfoList: [],
  selectedCardIndex: 0,
  debitCardDetailIdentity: defaultDebitCardDetailIdentity,
  view: defaultViewState,
}

export const DEBIT_CARD_DETAIL = 'DebitCardDetail'

export const GET_DEBIT_CARD_INFO_WITH_BENEFIT = `${DEBIT_CARD_DETAIL}/getDebitCardInfoWithBenefit`
export const GET_EZL_CARD_INFO = `${DEBIT_CARD_DETAIL}/getEzlCardInfo`
export const CHANGE_USAGE_OPTION = `${DEBIT_CARD_DETAIL}/changeUsageOption`
export const CHANGE_TRANSACTION_LIMIT = `${DEBIT_CARD_DETAIL}/changeTransactionLimit`
export const REPORT_CARD = `${DEBIT_CARD_DETAIL}/reportCard`
export const CANCEL_REPORT_CARD = `${DEBIT_CARD_DETAIL}/cancelReportCard`
export const CHANGE_DEBIT_CARD_PIN = `${DEBIT_CARD_DETAIL}/changeDebitCardPin`
export const SET_VIEW = `${DEBIT_CARD_DETAIL}/setView`
export const CHECK_UNLOCKED_STATUS = `${DEBIT_CARD_DETAIL}/checkUnlockedStatus`
export const CLEAR_STATE = `${DEBIT_CARD_DETAIL}/clearState`
export const CHANGE_DEBIT_CARD_IDENTITY = `${DEBIT_CARD_DETAIL}/changeDebitCardIdentity`
export const EZL_DELIVERY_COMPLETED = `${DEBIT_CARD_DETAIL}/ezlDeliveryCompleted`

export const debitCardDetailActions = {
  setView: createAction(SET_VIEW),
  checkUnlockedStatus: createAction(CHECK_UNLOCKED_STATUS),
  clearState: createAction(CLEAR_STATE),
  changeDebitCardIdentity: createAction(CHANGE_DEBIT_CARD_IDENTITY),
}

export const debitCardInfoWithBenefitAsync = createFetchAction(GET_DEBIT_CARD_INFO_WITH_BENEFIT)
export const ezlCardInfoAsync = createFetchAction(GET_EZL_CARD_INFO)
export const debitCardChangeUsageOptionAsync = createPostAction(CHANGE_USAGE_OPTION)
export const debitCardChangeTransactionLimitAsync = createPostAction(CHANGE_TRANSACTION_LIMIT)
export const debitCardReportAsync = createPostAction(REPORT_CARD)
export const debitCardCancelReportAsync = createPostAction(CANCEL_REPORT_CARD)
export const debitCardChangeDebitCardPinAsync = createPostAction(CHANGE_DEBIT_CARD_PIN)
export const ezlDeliveryCompletedAsync = createPostAction(EZL_DELIVERY_COMPLETED)

export const reducer = {
  [debitCardInfoWithBenefitAsync.SUCCESS]: apiSuccessReducer,
  [debitCardInfoWithBenefitAsync.FAILURE]: apiFailureReducer,
  [ezlCardInfoAsync.SUCCESS]: apiSuccessReducer,
  [ezlCardInfoAsync.FAILURE]: apiFailureReducer,
  [debitCardChangeUsageOptionAsync.FAILURE]: apiFailureReducer,
  [debitCardReportAsync.FAILURE]: apiFailureReducer,
  [debitCardCancelReportAsync.FAILURE]: apiFailureReducer,
  [SET_VIEW]: (state: IDebitCardDetailState, action: Action<any>) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CHANGE_DEBIT_CARD_IDENTITY]: (state: IDebitCardDetailState, action: Action<any>) => ({
    ...state,
    debitCardDetailIdentity: {
      ...state.debitCardDetailIdentity,
      ...action.payload,
    },
  }),
  [debitCardChangeDebitCardPinAsync.FAILURE]: apiFailureReducer,
  /**
   * DebitCardDetailIdentity shoud be
   * persist because the data is required
   * in the Debit Card Detail child component and when
   * user back to the Debit Card Detail after being
   * redirected to the other page from it
   * and the data will only change if the others page
   * redirected or push to DebitCardDetail page.
   */
  [CLEAR_STATE]: (state: IDebitCardDetailState) =>
    reset(state, {
      ...initialState,
      debitCardDetailIdentity: {
        ...state.debitCardDetailIdentity,
      },
    }),
}

export const debitCardDetailSelector = {
  debitCardDetailIdentity: (state: IRootState) =>
    oc(state[DEBIT_CARD_DETAIL]).debitCardDetailIdentity(defaultDebitCardDetailIdentity),
  debitCardInfo: (state: IRootState): IDebitCardInfo => {
    const { myCardIndex } = oc(state[DEBIT_CARD_DETAIL]).view(defaultViewState)
    const debitCardInfoList = oc(state[DEBIT_CARD_DETAIL]).debitCardInfoList([])

    return debitCardInfoList[myCardIndex] || EmptyDebitCardInfo
  },
  amount: (state: IRootState) => {
    const { additionalInfo } = debitCardDetailSelector.debitCardInfo(state)
    const { totalTransactionAmount = '0', totalBenefitAmount = '0' } = additionalInfo || {}

    return { totalTransactionAmount, totalBenefitAmount }
  },
  debitCardInfoList: (state: IRootState) => {
    return oc(state[DEBIT_CARD_DETAIL]).debitCardInfoList([])
  },
  view: (state: IRootState) => oc(state[DEBIT_CARD_DETAIL]).view(defaultViewState),
  isDisabledCardUsage: (state: IRootState) => {
    const isReported = debitCardDetailSelector.isReported(state)
    const isLocked = debitCardDetailSelector.isLocked(state)

    return isReported || isLocked
  },
  isDisabledTransactionLimit: (state: IRootState) => {
    const isReported = debitCardDetailSelector.isReported(state)
    const isLocked = debitCardDetailSelector.isLocked(state)
    const { isDomesticUse, isOverseaUse } = debitCardDetailSelector.view(state)

    return isReported || isLocked || (!isDomesticUse && !isOverseaUse)
  },
  shippingHistory: (state: IRootState) => {
    const cardInfo = debitCardDetailSelector.debitCardInfo(state)

    const dateByShippingStatus = [0, 0, 0, 0]

    if (!cardInfo || !cardInfo.shippingInfo) {
      return dateByShippingStatus
    }

    const { shippingInfo } = cardInfo
    const { shippingHistoryList } = shippingInfo

    return shippingHistoryList.reduce((list: number[], item) => {
      switch (item.shippingStatusTypeCode) {
        case ShippingStatusTypeCode.REQUESTED:
          list[0] = item.startedDate
          break
        case ShippingStatusTypeCode.MAKING:
          list[1] = item.startedDate
          break
        case ShippingStatusTypeCode.SHIPPING:
          list[2] = item.startedDate
          break
        case ShippingStatusTypeCode.ACTIVATION:
          list[3] = item.startedDate
          break
        default:
      }

      return list
    }, dateByShippingStatus)
  },
  shippingStatus: (state: IRootState) => {
    const debitCardInfo = debitCardDetailSelector.debitCardInfo(state)
    const { currentShippingStatusTypeCode = ShippingStatusTypeCode.REQUESTED } =
      oc(debitCardInfo).shippingInfo() || {}

    return currentShippingStatusTypeCode
  },
  isShippingDelayed: (state: IRootState) => {
    const debitCardInfo = debitCardDetailSelector.debitCardInfo(state)
    const {
      currentShippingStatusTypeCode = ShippingStatusTypeCode.REQUESTED,
      isShippingDelayed = false,
    } = oc(debitCardInfo).shippingInfo() || {}

    return currentShippingStatusTypeCode === ShippingStatusTypeCode.SHIPPING && isShippingDelayed
  },
  isShipping: (state: IRootState) => {
    const debitCardInfo = debitCardDetailSelector.debitCardInfo(state)
    const { debitCardStatusTypeCode: issueStatus = null } = debitCardInfo || {}

    return issueStatus === DebitCardStatusTypeCode.SHIPPING
  },
  isNeedActivation: (state: IRootState) => {
    const debitCardInfo = debitCardDetailSelector.debitCardInfo(state)
    const { debitCardStatusTypeCode: issueStatus = null } = debitCardInfo || {}

    return issueStatus === DebitCardStatusTypeCode.ACTIVATION
  },
  isReported: (state: IRootState) => {
    const debitCardInfo = debitCardDetailSelector.debitCardInfo(state)
    const { debitCardStatusTypeCode: issueStatus = null } = debitCardInfo || {}

    return issueStatus === DebitCardStatusTypeCode.LOST
  },
  isReissued: (state: IRootState) => {
    const debitCardInfoList = oc(state[DEBIT_CARD_DETAIL]).debitCardInfoList([])

    return debitCardInfoList.length > 1
  },
  isLocked: (state: IRootState) => {
    const debitCardInfo = debitCardDetailSelector.debitCardInfo(state)
    const { debitCardStatusTypeCode: issueStatus = null } = debitCardInfo || {}

    return issueStatus === DebitCardStatusTypeCode.LOCKED
  },
  leftDaysUntilCancelCardLostReport: (state: IRootState) => {
    const { additionalInfo } = debitCardDetailSelector.debitCardInfo(state)
    const { autoClosingDate = 0 } = additionalInfo || {}
    const endDate = getCancellableLastDate(autoClosingDate)
    const differenceDays = getDifferenceInDays(new Date(), endDate)

    return differenceDays
  },
}

export function* changeUsageOption(action: Action<IChangeUsageOptionAction>) {
  const { usageType, isUse } = action.payload

  try {
    yield put(debitCardDetailActions.setView({ [usageType]: isUse }))

    const { debitCardNo } = yield select(debitCardDetailSelector.debitCardInfo)
    const request = {
      debitCardNo,
      useYn: isUse ? 'Y' : 'N',
    }
    let apiUrl

    if (usageType === IS_DOMESTIC_USE) {
      apiUrl = DEBIT_CARD_API_URL.CHANGE_OPTION.DOMESTIC_USE
    } else {
      apiUrl = DEBIT_CARD_API_URL.CHANGE_OPTION.OVERSEA_USE
    }

    const { result }: IRequestAuthResult = yield call(Bridge.requestAuth, {
      hostType: HostType.BANK_API,
      apiUrl,
      request,
    })

    if (!result) {
      throw newError(STATUS_CODE.DEBIT_CARD_CLIENT_ERROR)
    }
    if (!result.isSuccess) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }
  } catch (e) {
    yield put(debitCardDetailActions.setView({ [usageType]: !isUse }))
    yield put(errorActions.set(e))
  }
}

const getChangeTransactionLimitParams = (
  debitCardNo: string,
  inputedAmount: string,
  singleTransactionLimitAmount: string,
  dailyLimitAmount: string,
  transactionType: string,
) => {
  const zero = '0'
  const currentSingleAmount = `${toInteger(singleTransactionLimitAmount)}`
  const currentDailyAmount = `${toInteger(dailyLimitAmount)}`
  // const isAllCurrentAmountsZero = currentSingleAmount === zero && currentDailyAmount === zero
  const isSingleAmount = transactionType === SINGLE_TRANSACTION_LIMIT_AMOUNT
  const isDailyAmount = transactionType === DAILY_LIMIT_AMOUNT

  if (
    /* 7301 발생
     * one-time-limit을 one-day-limit 보다 먼저 처리해 발생
     * 1일한도가 1회한도보다 작지만 않으면 정상이므로
     * one-time-limit, one-day-limit 둘 다 변경하는 조건에서 제외 */
    // isAllCurrentAmountsZero ||
    inputedAmount === zero ||
    (isDailyAmount && toInteger(inputedAmount) < toInteger(currentSingleAmount))
  ) {
    return {
      view: {
        singleTransactionLimitAmount: inputedAmount,
        dailyLimitAmount: inputedAmount,
      },
      params: [
        {
          hostType: HostType.BANK_API,
          apiUrl: DEBIT_CARD_API_URL.CHANGE_LIMIT.ONE_TIME_LIMIT,
          request: { debitCardNo, changedAmount: inputedAmount },
        },
        {
          hostType: HostType.BANK_API,
          apiUrl: DEBIT_CARD_API_URL.CHANGE_LIMIT.ONE_DAY_LIMIT,
          request: { debitCardNo, changedAmount: inputedAmount },
        },
      ],
    }
  }

  if (isSingleAmount) {
    const amount =
      toInteger(inputedAmount) > toInteger(currentDailyAmount) ? currentDailyAmount : inputedAmount

    return {
      view: { singleTransactionLimitAmount: amount },
      params: [
        {
          hostType: HostType.BANK_API,
          apiUrl: DEBIT_CARD_API_URL.CHANGE_LIMIT.ONE_TIME_LIMIT,
          request: { debitCardNo, changedAmount: amount },
        },
      ],
    }
  }

  return {
    view: { dailyLimitAmount: inputedAmount },
    params: [
      {
        hostType: HostType.BANK_API,
        apiUrl: DEBIT_CARD_API_URL.CHANGE_LIMIT.ONE_DAY_LIMIT,
        request: { debitCardNo, changedAmount: inputedAmount },
      },
    ],
  }
}

export function* changeTransactionLimit(action: Action<IChangeTransactionLimitAction>): any {
  const { transactionType, amount: inputedAmount } = action.payload
  const { singleTransactionLimitAmount, dailyLimitAmount } = yield select(
    debitCardDetailSelector.view,
  )

  try {
    const { debitCardNo } = yield select(debitCardDetailSelector.debitCardInfo)
    const { view, params } = getChangeTransactionLimitParams(
      debitCardNo,
      inputedAmount,
      singleTransactionLimitAmount,
      dailyLimitAmount,
      transactionType,
    )
    const needToChangeAllAmounts = params.length === 2

    yield put(debitCardDetailActions.setView(view))

    if (needToChangeAllAmounts) {
      const [oneTimeLimit, oneDayLimit] = yield all([
        call(Bridge.requestAuth, { ...params[0] }),
        call(Bridge.requestAuth, { ...params[1] }),
      ])

      if (!oneTimeLimit.result || !oneDayLimit.result) {
        throw newError(STATUS_CODE.DEBIT_CARD_CLIENT_ERROR)
      }
      if (!oneTimeLimit.result.isSuccess || !oneDayLimit.result.isSuccess) {
        /* code만 노출되었으나 message도 노출 */
        // const { code } = JSON.parse(oneTimeLimit.result.response)
        // throw newError(code)
        throw JSON.parse(oneTimeLimit.result.response)
      }
    } else {
      const { result }: IRequestAuthResult = yield call(Bridge.requestAuth, {
        ...params[0],
      })

      if (!result) {
        throw newError(STATUS_CODE.DEBIT_CARD_CLIENT_ERROR)
      }
      if (!result.isSuccess) {
        /* code만 노출되었으나 message도 노출 */
        // const { code } = JSON.parse(result.response)
        // throw newError(code)
        throw JSON.parse(result.response)
      }
    }
  } catch (e) {
    yield put(debitCardDetailActions.setView({ singleTransactionLimitAmount, dailyLimitAmount }))
    yield put(errorActions.set(e))
  }
}

export function* reportLostDebitCard() {
  try {
    const { debitCardNo, additionalInfo } = yield select(debitCardDetailSelector.debitCardInfo)
    const { linkedAcctNo: acctNo } = additionalInfo
    const { result }: IRequestPinAuthResult = yield call(Bridge.enterTransactionPinForDebitCard, {
      hostType: HostType.BANK_API,
      apiUrl: DEBIT_CARD_API_URL.REPORT_LOST,
      request: { debitCardNo },
    })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR
    ) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }

    if (result.action === RequestPinAuthAction.AUTH_SUCCESS) {
      const res = yield call(fetchDebitCardInfo, {
        debitCardNo,
        acctNo,
      })
      yield put(debitCardInfoWithBenefitAsync.success(res))
      yield call(scrollToTop, 0)
    }
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* cancelReportLostDebitCard() {
  try {
    const { debitCardNo, additionalInfo } = yield select(debitCardDetailSelector.debitCardInfo)
    const { linkedAcctNo: acctNo } = additionalInfo
    const { result }: IRequestPinAuthResult = yield call(Bridge.enterTransactionPinForDebitCard, {
      hostType: HostType.BANK_API,
      apiUrl: DEBIT_CARD_API_URL.CANCEL_REPORT_LOST,
      request: { debitCardNo },
    })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR
    ) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }

    if (result.action === RequestPinAuthAction.AUTH_SUCCESS) {
      const res = yield call(fetchDebitCardInfo, {
        debitCardNo,
        acctNo,
      })
      yield put(debitCardInfoWithBenefitAsync.success(res))
      yield call(scrollToTop, 0)
    }
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* changeDebitCardPin() {
  try {
    const { debitCardNo, additionalInfo } = yield select(debitCardDetailSelector.debitCardInfo)
    const { linkedAcctNo } = additionalInfo
    const { result }: IRequestPinAuthResult = yield call(Bridge.changeDebitCardPin, { debitCardNo })

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (result.action === RequestPinAuthAction.AUTH_FAIL) {
      yield call(push, DEBIT_CARD_ROUTES.PIN_LOCKED)

      return
    }
    if (result.action === RequestPinAuthAction.AUTH_ERROR) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)
      throw JSON.parse(result.response)
    }
    if (result.action === RequestPinAuthAction.FORGOT) {
      yield call(
        push,
        `${DEBIT_CARD_ROUTES.ACTIVATE}/${debitCardNo}/${linkedAcctNo}?${RESET_DEBIT_CARD_PIN_ROUTE_QUERY}`,
      )

      return
    }

    yield put(layerActions.open(NOTICE_ALERT_TYPE.CHANGE_DEBIT_CARD_PIN))
    yield call(scrollToTop, 0)
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* checkUnlockedStatus() {
  try {
    const { debitCardNo, additionalInfo } = yield select(debitCardDetailSelector.debitCardInfo)
    const { linkedAcctNo: acctNo } = additionalInfo

    const res = yield call(fetchDebitCardInfo, { debitCardNo, acctNo })
    yield put(debitCardInfoWithBenefitAsync.success(res))
    const isLocked = yield select(debitCardDetailSelector.isLocked)
    yield call(goBack)

    if (!isLocked) {
      yield call(changeDebitCardPin)
    }
  } catch (e) {
    yield put(errorActions.set(e))
  }
}

export function* getDebitCardInfo(action: Action<IDebitCardShowInfoRequest>) {
  const request = action.payload
  /**
   * 앱에서 바로 호출 될 경우 (isMobile=true),
   * redux에 isEZL 저장되는 시점과 싱크 안맞음 -> 기존 debit card info logic 수행
   * ==> Module 단계에서 url querystring의 cardType 정보 직접 가져옴 (isEzlParam)
   * */
  const urlParams = new URLSearchParams(window.location.search)
  const cardType = urlParams.get('cardType')
  const isEzlParam = cardType === 'ezl'
  try {
    const { isEZL } = yield select(debitCardIssueSelector.mode) // after issuance step
    if (isEZL || isEzlParam) {
      yield put(ezlCardInfoAsync.fetch())

      return
    }
    const res: IDebitCardShowInfoResponse = yield call(fetchDebitCardInfo, request)
    const { debitCardInfoList, selectedCardIndex } = res

    if (debitCardInfoList[0] && debitCardInfoList[0].additionalInfo) {
      const {
        isOverseaUse,
        isDomesticUse,
        singleTransactionLimitAmount,
        dailyLimitAmount,
      } = debitCardInfoList[0].additionalInfo

      yield put(
        debitCardDetailActions.setView({
          myCardIndex: selectedCardIndex,
          isDomesticUse,
          isOverseaUse,
          singleTransactionLimitAmount: `${toInteger(singleTransactionLimitAmount)}`,
          dailyLimitAmount: `${toInteger(dailyLimitAmount)}`,
        }),
      )
    }

    yield put(debitCardInfoWithBenefitAsync.success(res))
  } catch (e) {
    yield put(debitCardInfoWithBenefitAsync.failure(e))
    yield put(errorActions.set(e))
  }
}

export function* getEzlCardInfo() {
  yield put(loadingActions.start(DEBIT_CARD_DETAIL))
  const urlParams = new URLSearchParams(window.location.search)
  const isMobile = urlParams.get('isMobile')
  try {
    if (isMobile) {
      const { list }: IDebitCardEzlAvailableIssueResponse = yield call(inquiryEzlCardIssuanceStatus)
      const { debitCardNo } = list.find(
        detail => detail.debitCardStatus === EzlCardStatus.NOT_READY,
      ) as IEzlCardDetail
      yield put(debitCardDetailActions.changeDebitCardIdentity({ debitCardNo }))
    }
    const { debitCardNo: medNo } = yield select(debitCardDetailSelector.debitCardDetailIdentity)

    const res: IDebitCardEzlInquiryResponse = yield call(ezlInquiry, {
      medNo,
    } as IDebitCardEzlInquiryRequest)
    const ezlCardInfo: IDebitCardInfo = {
      debitCardNo: res.medNo,
      debitCardStatusTypeCode: res.cardStatusTypeCode,
      debitCardDesignTypeCode: res.debitCardDesignTypeCode,
      debitCardBrand: DebitCardBrand.D,
      debitCardVersion: res.crdVersion,
      shippingInfo: {
        currentShippingStatusTypeCode: res.currentShippingStatusTypeCode,
        shippingAddress: res.shippingAddress,
        shippingHistoryList: res.shippingHistoryList,
        shippingVendorInfo: res.shippingVendorInfo,
        isShippingDelayed: false,
      },
    }
    yield put(ezlCardInfoAsync.success({ debitCardInfoList: [ezlCardInfo] }))
  } catch (e) {
    yield put(ezlCardInfoAsync.failure(e))
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(DEBIT_CARD_DETAIL))
  }
}

function* setEzlDeliveryCompleted() {
  yield put(loadingActions.start(DEBIT_CARD_DETAIL))
  try {
    const { debitCardNo, debitCardStatusTypeCode: debitCardStatus }: IDebitCardInfo = yield select(
      debitCardDetailSelector.debitCardInfo,
    )
    const payload: IDebitCardEzlDeliveryCompleteRequest = {
      debitCardNo,
      debitCardStatus,
    }
    yield call(isEzlDeliveryComplete, payload)
    yield call(Bridge.closeWebView)
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(DEBIT_CARD_DETAIL))
  }
}

export const debitCardDetailReducer = handleActions(reducer, initialState)
export const debitCardDetailSaga = [
  takeLatest(debitCardInfoWithBenefitAsync.FETCH, getDebitCardInfo),
  takeLatest(ezlCardInfoAsync.FETCH, getEzlCardInfo),
  takeLeading(debitCardChangeUsageOptionAsync.POST, changeUsageOption),
  takeLeading(debitCardChangeTransactionLimitAsync.POST, changeTransactionLimit),
  takeLeading(debitCardReportAsync.POST, reportLostDebitCard),
  takeLeading(debitCardCancelReportAsync.POST, cancelReportLostDebitCard),
  takeLeading(debitCardChangeDebitCardPinAsync.POST, changeDebitCardPin),
  takeLatest(CHECK_UNLOCKED_STATUS, checkUnlockedStatus),
  takeLatest(ezlDeliveryCompletedAsync.POST, setEzlDeliveryCompleted),
]
